import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { fmtDate, fmtRelative } from '../shared/format'
import { UpdatePanel } from '../updates/UpdatePanel'
import { useInstallation } from './api'

export function InstallationDetailPage() {
  const { id = '' } = useParams()
  const { t } = useTranslation()
  const detail = useInstallation(id)

  if (detail.isError) return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
  const inst = detail.data?.installation
  const certs = detail.data?.certificates ?? []

  return (
    <>
      <PageHeader
        backTo="/installations"
        title={inst ? inst.name : <Skeleton className="h-8 w-48" />}
        description={inst ? `${inst.environment} · ${inst.hosting}` : undefined}
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-muted-foreground text-sm font-medium">
              {t('installations.columns.status')}
            </CardTitle>
          </CardHeader>
          <CardContent>{inst ? <Badge>{inst.status}</Badge> : <Skeleton className="h-5 w-20" />}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-muted-foreground text-sm font-medium">
              {t('installations.columns.health')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {inst ? (
              <Badge tone={inst.license_state === 'licensed' ? 'success' : 'danger'}>{inst.license_state}</Badge>
            ) : (
              <Skeleton className="h-5 w-20" />
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-muted-foreground text-sm font-medium">
              {t('installations.columns.heartbeat')}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {inst ? (
              inst.last_heartbeat_at ? (
                fmtRelative(inst.last_heartbeat_at)
              ) : (
                '—'
              )
            ) : (
              <Skeleton className="h-5 w-24" />
            )}
          </CardContent>
        </Card>
      </div>

      {inst ? <UpdatePanel inst={inst} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('installations.certs')}</CardTitle>
        </CardHeader>
        <CardContent>
          {detail.isLoading ? (
            <Skeleton className="h-32" />
          ) : certs.length === 0 ? (
            <EmptyState title={t('installations.noCerts')} />
          ) : (
            <Table>
              <THead>
                <TR className="hover:bg-transparent">
                  <TH>{t('installations.certSerial')}</TH>
                  <TH>{t('installations.columns.status')}</TH>
                  <TH>{t('installations.certValid')}</TH>
                </TR>
              </THead>
              <TBody>
                {certs.map((c) => (
                  <TR key={c.id}>
                    <TD className="font-mono text-xs">{c.serial || c.fingerprint_sha256.slice(0, 16)}</TD>
                    <TD>
                      <Badge tone={c.status === 'revoked' ? 'danger' : 'success'}>{c.status}</Badge>
                    </TD>
                    <TD className="text-muted-foreground">{c.not_after ? fmtDate(c.not_after) : '—'}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  )
}
